import { createApp } from 'vue'
import { Modal, Tooltip } from 'bootstrap'
import swal from 'sweetalert'
import PasswordInput from '../common/components/PasswordInput'
import PhoneInput from '../common/components/PhoneInput'
import { getErrorMessageFromResponse } from '../common/helpers.js'

window.epb = window.epb || {}

// Subscribe form.
if (document.getElementById('subscribe-form')) {

  const app = createApp({
    components: {
      'password-input': PasswordInput,
      'phone-input': PhoneInput,
    },
    data() {
      return {
        currentView: 'subscriptions-selections', // subscriptions-selections, registration, payment
        isLoading: false,
        isSubmitting: false,
        isValidatingCoupon: false,
        terms: [],
        pricingCalculatorUtilities: [],
        utilityHubs: [],
        states: [],
        timezones: [],
        products: {},
        stripe: {
          clientSecret: null,
          cardNumber: null,
          setupIntent: null,
        },
        discount: 0,
        minCouponCodeLength: 8,
        formData: {
          products: {
            'pricing-calculator': {
              product: 'premium',
              utilities: [],
            },
            'pricing-reports': {
              product: null,
            },
            'energy-price-monitor': {
              product: 'energy-price-monitor-default',
              utilityHubs: [],
            },
          },
          user: {
            firstName: null,
            lastName: null,
            company: null,
            email: null,
            phone: null,
            address: null,
            address2: null,
            city: null,
            state: null,
            zip: null,
            password: null,
            passwordConfirmation: null,
            timezone: null,
            profession: null,
            acceptTerms: 0,
          },
          payment: {
            paymentMethod: null,
            billingFirstName: null,
            billingLastName: null,
            billingAddressSame: 1,
            billingAddress: null,
            billingAddress2: null,
            billingCity: null,
            billingState: null,
            coupon: null,
          },
        },
      }
    },
    computed: {
      userTimezoneLabel() {
        if (this.formData.user.timezone) {
          return this.timezones[ this.formData.user.timezone ]
        }
      },

      // Power Pricing Reports
      pricingReportProducts() {
        return {
          'power-pricing-reports-up-to-5': 'Up to 5 Hubs',
          'power-pricing-reports-up-to-10': 'Up to 10 Hubs',
          'power-pricing-reports-up-to-20': 'Up to 20 Hubs',
          'power-pricing-reports-up-to-30': 'Up to 30 Hubs',
          'power-pricing-reports-30-plus': '30+ Hubs',
        }
      },
      pricingReportsSubtotal() {
        let total = 0

        const productSlug = this.formData.products['pricing-reports'].product
        if (productSlug) {
          return this.getPricingReportsProductMonthlyCost(productSlug)
        }

        return parseFloat(total)
      },

      // Pricing Calculator (a.k.a. All-in Price Monitor)
      pricingCalculatorMonthlyCost() {
        if (
          this.products['pricing-calculator'] &&
          this.products['pricing-calculator'].premium &&
          this.products['pricing-calculator'].premium.cost_monthly
        ) {
          return parseFloat(this.products['pricing-calculator'].premium.cost_monthly)
        }
      },
      pricingCalculatorSubtotal() {
        if (this.formData.products['pricing-calculator'].utilities.length > 0) {
          return this.formData.products['pricing-calculator'].utilities.length * this.pricingCalculatorMonthlyCost
        }
        return 0;
      },

      // Energy Price Monitor
      energyPriceMonitorMonthlyCost() {
        return parseFloat(this.products['energy-price-monitor']['energy-price-monitor-default'].cost_monthly)
      },
      energyPriceMonitorSubtotal() {
        if (this.formData.products['energy-price-monitor'].utilityHubs.length > 0) {
          return this.formData.products['energy-price-monitor'].utilityHubs.length * this.energyPriceMonitorMonthlyCost
        }
        return 0;
      },

      // Subtotal and total
      subtotalCost() {
        return this.pricingReportsSubtotal + this.pricingCalculatorSubtotal + this.energyPriceMonitorSubtotal
      },
      totalCost() {
        return this.subtotalCost - this.discount
      },

      // Select all checkboxes
      epmSelectAllCheckboxIsChecked() {
        return this.formData.products['energy-price-monitor'].utilityHubs.length == this.utilityHubs.length
      },
      apmSelectAllCheckboxIsChecked() {
        return this.formData.products['pricing-calculator'].utilities.length == this.pricingCalculatorUtilities.length
      },
    },
    async mounted() {
      this.isLoading = true

      // Load the config data.
      const resp = await axios.get('/subscribe/config')

      this.stripe.clientSecret = resp.data.stripeClientSecret
      this.terms = resp.data.terms
      this.pricingCalculatorUtilities = resp.data.pricingCalculatorUtilities
      this.utilityHubs = resp.data.utilityHubs
      this.states = resp.data.states
      this.timezones = resp.data.timezones
      this.products = resp.data.products

      this.bootstrap()

      this.isLoading = false
    },
    watch: {
      'formData.products.pricing-reports.plan'() {
        if (! this.formData.products['pricing-reports'].selections.includes('power-724-rtc-pricing-reports')) {
          this.formData.products['pricing-reports'].selections.push('power-724-rtc-pricing-reports')
        }
      }
    },
    methods: {
      bootstrap() {

        // Tooltips.
        const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'))
        const tooltipList = tooltipTriggerList.map(tooltipTriggerEl => {
          return new Tooltip(tooltipTriggerEl)
        })
      },
      printTerms(type) {
        frames[type].print()
      },
      getPricingReportItem(productSlug) {
        if (this.products['pricing-reports'] && this.products['pricing-reports'][productSlug]) {
          return this.products['pricing-reports'][productSlug]
        }
      },
      getPricingReportsProductMonthlyCost(productSlug) {
        const product = this.getPricingReportItem(productSlug)
        if (product) {
          return parseFloat(product.cost_monthly)
        }
      },
      formatPhoneNumber(phoneNumber) {
        const cleaned = ('' + phoneNumber).replace(/\D/g, '')
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)

        if (match) {
          return '(' + match[1] + ') ' + match[2] + '-' + match[3]
        }

        return phoneNumber
      },

      // Select all checkboxes
      handleEpmSelectAllCheckboxClick() {
        if (this.formData.products['energy-price-monitor'].utilityHubs.length == this.utilityHubs.length) {
          this.formData.products['energy-price-monitor'].utilityHubs = []
        } else {
          this.formData.products['energy-price-monitor'].utilityHubs = this.utilityHubs.map(utilityHub => utilityHub.id)
        }
      },
      handleApmSelectAllCheckboxClick() {
        if (this.formData.products['pricing-calculator'].utilities.length == this.pricingCalculatorUtilities.length) {
          this.formData.products['pricing-calculator'].utilities = []
        } else {
          this.formData.products['pricing-calculator'].utilities = this.pricingCalculatorUtilities.map(utility => utility.id)
        }
      },

      goBackToView(view) {
        this.currentView = view
      },
      progressToView(view) {

        // Validation
        switch(view) {
          case 'registration':

            if (
              ! this.formData.products['pricing-reports'].product &&
              this.formData.products['pricing-calculator'].utilities.length <= 0 &&
              this.formData.products['energy-price-monitor'].utilityHubs.length <= 0
            ) {
              swal('', 'Please select either a Power Pricing Reports product, any All-in Price Monitor utilities, and/or any Energy Price Monitor utility hubs.')
              return
            }

            break

          case 'payment':
            if (
              ! this.formData.user.firstName ||
              ! this.formData.user.lastName ||
              ! this.formData.user.company ||
              ! this.formData.user.email ||
              ! this.formData.user.phone ||
              ! this.formData.user.address ||
              ! this.formData.user.city ||
              ! this.formData.user.state ||
              ! this.formData.user.zip ||
              ! this.formData.user.password ||
              ! this.formData.user.passwordConfirmation ||
              ! this.formData.user.timezone ||
              ! this.formData.user.profession
            ) {
              swal('', 'Please complete all required selections.')
              return
            }

            // Account for phone number formatting or no formatting.
            if (this.formData.user.phone.replace(/\D/g,'').length != 10) {
              swal('', 'Please enter a valid US phone number.')
              return
            }

            if (this.formData.user.zip.length != 5) {
              swal('', 'The ZIP/Postal code must be 5 characters long.')
              return
            }

            if (this.formData.user.password !== this.formData.user.passwordConfirmation) {
              swal('', 'The passwords do not match.')
              return
            }

            if (this.formData.user.password.length < 8) {
              swal('', 'The password must be a minimum of 8 characters.')
              return
            }

            if (this.formData.user.acceptTerms != 1) {
              swal('', 'You must agree to the Terms of Use.')
              return
            }

            // Re-validate/apply the coupon in case a coupon code was 
            // already used, but then the user changed their product selections.
            this.validateCoupon()

            break
        }

        this.currentView = view

        // Allow time for transition to complete.
        setTimeout(() => {
          this.bootstrap()

          // Setup Stripe.
          if (view == 'payment') {
            this.setupStripe()
          }
        }, 750)
      },
      canProgressToPayment() {
        if (
          this.formData.user.firstName &&
          this.formData.user.lastName &&
          this.formData.user.company &&
          this.formData.user.email &&
          this.formData.user.phone &&
          this.formData.user.phone.replace(/\D/g,'').substr(0, 10).length == 10 &&
          this.formData.user.address &&
          this.formData.user.city &&
          this.formData.user.state &&
          this.formData.user.zip &&
          this.formData.user.password &&
          this.formData.user.passwordConfirmation &&
          this.formData.user.timezone &&
          this.formData.user.profession &&
          this.formData.user.acceptTerms == 1
        ) {
          return true
        }
        return false
      },
      canSubmit() {
        return this.formData.payment.billingFirstName &&
               this.formData.payment.billingLastName && (
                 this.formData.payment.billingAddressSame == 1 || 
                 (
                   this.formData.payment.billingAddressSame == 0 &&
                   this.formData.payment.billingAddress && 
                   this.formData.payment.billingCity && 
                   this.formData.payment.billingState
                 )
               )
      },
      async validateCoupon() {

        // Coupon codes must be at least x characters long.
        if (
          !this.formData.payment.coupon || 
          this.formData.payment.coupon.length < this.minCouponCodeLength
        ) {
          return
        }

        this.isValidatingCoupon = true

        try {

          // Compile the payload.
          let payload = {
            payment: {
              coupon: this.formData.payment.coupon,
            },
            products: {...this.formData.products},
          }
          payload = JSON.parse(JSON.stringify(payload))

          // Validate
          const resp = await axios.post('/subscribe/validate-coupon', payload)

          this.isValidatingCoupon = false
          this.discount = 0

          if (resp.data?.discount && resp.data.discount > 0) {
            this.discount = resp.data.discount
          }

        } catch (err) {
          this.isValidatingCoupon = false
          this.discount = 0

          const swalContent = document.createElement('div')
          swalContent.innerHTML = getErrorMessageFromResponse(err)

          swal({
            content: swalContent,
          })
        }
      },
      handleSubmit() {
        this.isSubmitting = true
        this.createStripePaymentMethodIdentifier()
      },
      /**
       * Setup Stripe.
       */
      setupStripe() {

        // Stripe elements.
        const elements = stripe.elements()

        this.stripe.cardNumber = elements.create('cardNumber')
        this.stripe.cardNumber.mount('#cardNumber-element')

        const cardExpiry = elements.create('cardExpiry')
        cardExpiry.mount('#cardExpiry-element')

        const cardCvc = elements.create('cardCvc')
        cardCvc.mount('#cardCvc-element')

        const postalCode = elements.create('postalCode')
        postalCode.mount('#postalCode-element')

        // Validate the inputs.
        const inputs = [this.stripe.cardNumber, cardExpiry, cardCvc, postalCode]

        for (let i = 0; i < inputs.length; i++) {
          inputs[i].addEventListener('change', function(event) {
            let displayError = document.getElementById(event.elementType + '-errors')
            if (event.error) {
              displayError.textContent = event.error.message
            } else {
              displayError.textContent = ''
            }
          })
        }
      },
      /** 
       * Create the Stripe Payment Method Identifier.
       */
      createStripePaymentMethodIdentifier() {

        // Gather the billing info.
        let billingInfo = {
          firstName: this.formData.payment.billingFirstName,
          lastName: this.formData.payment.billingLastName,
          country: 'US',
        }

        if (this.formData.payment.billingAddressSame == 1) {
          billingInfo.address1 = this.formData.user.address
          billingInfo.address2 = this.formData.user.address2
          billingInfo.city = this.formData.user.city
          billingInfo.state = this.formData.user.state
        } else {
          billingInfo.address1 = this.formData.payment.billingAddress
          billingInfo.address2 = this.formData.payment.billingAddress2
          billingInfo.city = this.formData.payment.billingCity
          billingInfo.state = this.formData.payment.billingState
        }

        // Compile an object of billing info for Stripe.
        const stripeBillingDetails = {
          name: (billingInfo.firstName && billingInfo.lastName) ? billingInfo.firstName + ' ' + billingInfo.lastName : undefined,
          address: {
            line1:   billingInfo.address1 || undefined,
            line2:   billingInfo.address2 || undefined,
            city:    billingInfo.city || undefined,
            state:   (billingInfo.state && billingInfo.country == 'US') ? billingInfo.state : undefined,
            country: billingInfo.country,
          }
        }

        // Validate the required info.
        if (
          stripeBillingDetails['name'] == undefined || stripeBillingDetails['name'] == '' ||
          stripeBillingDetails['address'] == undefined || stripeBillingDetails['address'] == '' ||
          stripeBillingDetails['address']['line1'] == undefined || stripeBillingDetails['address']['line1'] == '' ||
          stripeBillingDetails['address']['city'] == undefined || stripeBillingDetails['address']['city'] == '' ||
          stripeBillingDetails['address']['state'] == undefined || stripeBillingDetails['address']['state'] == ''
        ) {
          this.isSubmitting = false
          swal('', 'Please enter all required payment fields.')
          return false
        }

        // Stripe Setup Intent
        // See: https://stripe.com/docs/js/setup_intents/confirm_card_setup
        if (this.stripe.setupIntent) {
          this.stripeSetupIntentHandler()

        } else {
          stripe.confirmCardSetup(this.stripe.clientSecret, {
            payment_method: {
              card: this.stripe.cardNumber,
              billing_details: stripeBillingDetails,
            }
          })
          .then(result => {
            if (result.error) {
              this.isSubmitting = false
              swal('', result.error.message)

            } else {
              this.stripe.setupIntent = result.setupIntent
              this.stripeSetupIntentHandler()
            }
          })
        }
      },
      /**
       * Submit the form data for processing.
       */
      async stripeSetupIntentHandler() {
        this.formData.payment.paymentMethod = this.stripe.setupIntent.payment_method

        // Compile the payload.
        const payload = JSON.parse(JSON.stringify(this.formData))

        payload.totals = {
          'pricing-reports': this.pricingReportsSubtotal,
          'energy-price-monitor': this.energyPriceMonitorSubtotal,
          'pricing-calculator': this.pricingCalculatorSubtotal,
          total: this.subtotalCost,
        }

        try {

          // Submit the payload.
          const resp = await axios.post('/subscribe', payload)

          // Redirect
          window.location.href = resp.data?.redirectUrl || '/app'

        } catch (err) {
          this.isSubmitting = false

          const swalContent = document.createElement('div')
          swalContent.innerHTML= getErrorMessageFromResponse(err)

          swal({
            content: swalContent,
          })
        }
      },
    }
  })

  app.mount('#subscribe-form')
}
